import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import api from "@/lib/api";

// Lazy, desktop-only, muted looping hero preview. Falls back to the poster image on
// mobile, when prefers-reduced-motion is set, or if autoplay is blocked. Keeps CWV clean.
export default function HeroPreview({ videoId, poster }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    setShow(false);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const desktop = window.matchMedia("(min-width: 1024px)").matches;
    if (reduce || !desktop || !videoId) return;

    let hls, cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/videos/${videoId}/playback`);
        if (cancelled) return;
        const el = ref.current;
        if (!el) return;
        el.muted = true;
        if (Hls.isSupported()) {
          hls = new Hls({ maxBufferLength: 12, capLevelToPlayerSize: true });
          hls.loadSource(data.playback_url);
          hls.attachMedia(el);
        } else if (el.canPlayType("application/vnd.apple.mpegurl")) {
          el.src = data.playback_url;
        } else return;
        try { await el.play(); setShow(true); } catch (_) { setShow(false); }
      } catch (_) {}
    }, 1400);

    const onVis = () => {
      const el = ref.current;
      if (!el) return;
      if (document.hidden) el.pause(); else el.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
      if (hls) hls.destroy();
    };
  }, [videoId]);

  return (
    <video
      ref={ref}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
      data-testid="hero-preview"
      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${show ? "opacity-100" : "opacity-0"}`}
    />
  );
}
